import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-500" />
            <h1 className="text-lg font-semibold text-white">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <div className="text-sm text-gray-500">
          Last updated: March 25, 2026
        </div>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">1. Introduction</h2>
          <p>
            ProsperMind ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our financial wellness application and services.
          </p>
          <p>
            By using ProsperMind, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our services.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">2. Information We Collect</h2>
          
          <h3 className="text-lg font-medium text-gray-300">2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Name and email address</li>
            <li>Account credentials</li>
            <li>Profile information</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-300">2.2 Financial Information</h3>
          <p>With your explicit consent, we collect:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Income and expense data</li>
            <li>Asset and liability information</li>
            <li>Financial goals and preferences</li>
            <li>Bank statement data (when you upload statements)</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-300">2.3 Usage Information</h3>
          <p>We automatically collect:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Device and browser information</li>
            <li>IP address and location data</li>
            <li>Usage patterns and interactions</li>
            <li>Cookies and similar technologies</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Provide and maintain our services</li>
            <li>Generate personalized financial insights using AI</li>
            <li>Track your financial progress and goals</li>
            <li>Improve and optimize our services</li>
            <li>Communicate with you about your account</li>
            <li>Send marketing communications (with your consent)</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">4. AI-Generated Insights</h2>
          <p>
            ProsperMind uses artificial intelligence to analyze your financial data and generate personalized insights. These insights are:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Generated automatically based on your data</li>
            <li>For informational purposes only</li>
            <li>Not financial, investment, or tax advice</li>
            <li>Not a substitute for professional financial advice</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">5. Data Sharing</h2>
          <p className="font-medium text-yellow-500">We never sell your personal data.</p>
          <p>We may share your information with:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Service providers who assist in operating our platform</li>
            <li>Legal authorities when required by law</li>
            <li>Business partners with your explicit consent</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">6. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Encryption of data in transit and at rest</li>
            <li>Secure server infrastructure</li>
            <li>Regular security audits</li>
            <li>Access controls and authentication</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your data</li>
            <li>Export your data</li>
            <li>Withdraw consent at any time</li>
            <li>Opt-out of marketing communications</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">8. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your data at any time by contacting us or through your account settings.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">9. International Data Transfers</h2>
          <p>
            Your data may be processed in the United States and other countries. We ensure appropriate safeguards are in place for international transfers.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">10. Children's Privacy</h2>
          <p>
            ProsperMind is not intended for users under 18 years of age. We do not knowingly collect data from children.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-white">12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <p className="text-yellow-500">privacy@prospermind.com</p>
        </section>

        {/* Footer spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
};

export default PrivacyPolicy;
